import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { DocCard, DocLightbox, useSignedDocs } from '../../components/admin2/DocViewer';
import SupplierOutreach from '../../components/admin2/SupplierOutreach';
import SupplierActivity from '../../components/admin2/SupplierActivity';
import { printVerificationFile } from '../../lib/verificationFilePrint';
import AdminNoteThread from '../../components/admin/AdminNoteThread';
import {
  QUEUE_TABS,
  DECISIONS,
  applyVerificationDecision,
  buildDocList,
  buildSupplierJourney,
  fetchReviewHistory,
  fetchSupplierProducts,
  fetchVerificationQueue,
} from '../../lib/consoleVerification';

// Factory Verification — the whole review happens on this one screen.
//
// Before this page, a verification file could only be reviewed in the legacy
// /admin console (organic suppliers) or nowhere at all (a claimed factory opens
// its archival factory tab, which carries no verification file). Both are still
// reachable; neither is where the work happens now.

const nf = (v) => (v === null || v === undefined ? '' : String(v).trim());

const fmtDate = (v, isAr) => (v ? new Date(v).toLocaleDateString(isAr ? 'ar' : 'en', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const daysSince = (v) => (v ? Math.max(0, Math.floor((Date.now() - new Date(v).getTime()) / 86400000)) : null);

const STATUS_TONE = {
  verified: 'ok',
  verification_under_review: 'warn',
  pending: 'warn',
  under_review: 'warn',
  submitted: 'warn',
  review: 'warn',
  verification_required: 'info',
  registered: 'neutral',
  rejected: 'danger',
  inactive: 'neutral',
};

function StatusBadge({ status, isAr }) {
  const raw = nf(status).toLowerCase();
  const tone = STATUS_TONE[raw] || 'neutral';
  const label = {
    verified: isAr ? 'موثّق' : 'Verified',
    verification_under_review: isAr ? 'تحت المراجعة' : 'Under review',
    pending: isAr ? 'معلّق (قديم)' : 'Pending (legacy)',
    verification_required: isAr ? 'بانتظار المورّد' : 'Awaiting supplier',
    rejected: isAr ? 'مرفوض' : 'Rejected',
    registered: isAr ? 'مسجَّل' : 'Registered',
  }[raw] || raw || '—';
  return <span className={`ac-badge ${tone}`}><span className="dot" />{label}</span>;
}

function KV({ k, v, mono, link }) {
  if (!nf(v)) return null;
  return (
    <div className="ac-kv">
      <div className="ac-kv-k">{k}</div>
      <div className="ac-kv-v" style={mono ? { fontVariantNumeric: 'tabular-nums' } : undefined} dir={mono || link ? 'ltr' : undefined}>
        {link ? <a href={/^https?:\/\//i.test(v) ? v : `https://${v}`} target="_blank" rel="noreferrer noopener">{v}</a> : v}
      </div>
    </div>
  );
}

// ── Queue list ──────────────────────────────────────────────────────────────
function QueueRow({ row, active, onOpen, isAr }) {
  const name = nf(row.company_name) || nf(row.full_name) || (isAr ? 'بلا اسم' : 'Unnamed');
  const waiting = daysSince(row.created_at);
  return (
    <button type="button" className={`acv-row${active ? ' on' : ''}`} onClick={() => onOpen(row.id)}>
      {row.avatar_url
        ? <img className="acv-row-logo" src={row.avatar_url} alt="" />
        : <span className="acv-row-logo fb">{name.charAt(0).toUpperCase()}</span>}
      <span className="acv-row-body">
        <span className="acv-row-name" title={name}>{name}</span>
        <span className="acv-row-meta">
          {[nf(row.city), nf(row.country)].filter(Boolean).join('، ') || '—'}
          {row.origin === 'factory_claim' && <span className="acv-tag">{isAr ? 'مطالبة' : 'Claim'}</span>}
        </span>
        <span className="acv-row-meta">
          {isAr ? `${row.docCount} وثيقة` : `${row.docCount} docs`}
          <span className="acv-dot-sep">·</span>
          {isAr ? `${row.completeness.done}/${row.completeness.total} حقول` : `${row.completeness.done}/${row.completeness.total} fields`}
          {waiting !== null && <><span className="acv-dot-sep">·</span>{isAr ? `${waiting} يوم` : `${waiting}d`}</>}
        </span>
      </span>
    </button>
  );
}

// ── Journey ─────────────────────────────────────────────────────────────────
// Where this supplier got to, when, and how long he has been sitting there. The
// reviewer's first question, and until now it had to be reassembled by eye from
// a status badge, a registration date and a document count in three places.
//
// "Submitted" shows as done with no date on purpose: the status proves it
// happened, submit_supplier_verification() records no time, and a made-up date on
// a review screen is worse than an honest blank.
const STAGE_LABEL = {
  registered: { ar: 'سجّل', en: 'Registered' },
  uploaded: { ar: 'رفع وثائق', en: 'Uploaded documents' },
  submitted: { ar: 'أرسل للمراجعة', en: 'Submitted for review' },
  decided: { ar: 'صدر قرار', en: 'Decision issued' },
};

const DECISION_LABEL = {
  supplier_verified: { ar: 'توثيق', en: 'Verified' },
  supplier_rejected: { ar: 'رفض', en: 'Rejected' },
  supplier_more_info_required: { ar: 'طلب استكمال', en: 'More info requested' },
};

// Arabic does not pluralise by appending a number. "منذ 0 يومًا" and "منذ 2 يومًا"
// are both wrong, and on a screen written for an Arabic-speaking reviewer that
// reads as machine output rather than as a sentence.
function stuckAr(n) {
  if (n <= 0) return 'آخر حركة اليوم';
  if (n === 1) return 'بلا حركة منذ يوم';
  if (n === 2) return 'بلا حركة منذ يومين';
  if (n <= 10) return `بلا حركة منذ ${n} أيام`;
  return `بلا حركة منذ ${n} يومًا`;
}

function JourneyCard({ journey, isAr }) {
  return (
    <div className="ac-card">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <p className="ac-card-title">{isAr ? 'مسار المورّد' : 'Supplier journey'}</p>
        {journey.stuckDays !== null && (
          <p className={`acv-stuck${journey.stuckDays >= 7 ? ' hot' : ''}`}>
            {isAr ? stuckAr(journey.stuckDays) : (journey.stuckDays === 0 ? 'No movement today' : `No movement for ${journey.stuckDays}d`)}
          </p>
        )}
      </div>
      <ol className="acv-journey">
        {journey.stages.map((st) => {
          const label = STAGE_LABEL[st.key];
          const decision = st.key === 'decided' && st.action ? DECISION_LABEL[st.action] : null;
          return (
            <li key={st.key} className={`acv-jstage${st.done ? ' done' : ''}`}>
              <span className="acv-jdot" aria-hidden="true" />
              <span className="acv-jbody">
                <span className="acv-jlabel">
                  {isAr ? label.ar : label.en}
                  {st.key === 'uploaded' && st.done ? ` · ${st.count}` : ''}
                  {decision ? ` — ${isAr ? decision.ar : decision.en}` : ''}
                </span>
                <span className="acv-jwhen">
                  {st.at
                    ? fmtDate(st.at, isAr)
                    : st.timeUnknown
                      ? (isAr ? 'التاريخ غير مسجَّل' : 'time not recorded')
                      : st.legacyUnknown
                        ? (isAr ? 'غير معروف — حساب قديم' : 'unknown — legacy account')
                        : st.done ? '—' : (isAr ? 'لم يحدث' : 'not yet')}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ── Decision modal ──────────────────────────────────────────────────────────
function DecisionModal({ decision, supplier, missingFields, onCancel, onConfirm, busy, isAr }) {
  const [note, setNote] = useState('');
  const spec = DECISIONS[decision];
  const title = {
    approve: isAr ? 'توثيق هذا المصنع' : 'Verify this factory',
    more_info: isAr ? 'طلب استكمال' : 'Request more information',
    reject: isAr ? 'رفض الطلب' : 'Reject this application',
  }[decision];
  const blurb = {
    approve: isAr
      ? 'ستتحول الحالة إلى "موثّق"، وتُرسل رسالة القبول للمورّد.'
      : 'The account becomes Verified and the approval email is sent to the supplier.',
    more_info: isAr
      ? 'يعود المورّد لإكمال ملفه، وتصله رسالة بذلك. اكتب بالضبط ما ينقصه — هذا ما سيقرأه.'
      : 'The supplier is sent back to complete their file and emailed about it. Write exactly what is missing — this is what they will read.',
    reject: isAr
      ? 'يُرفض الحساب نهائياً وتصله رسالة. السبب يُحفظ في سجل المراجعة.'
      : 'The account is rejected and the supplier is emailed. The reason is kept in the review log.',
  }[decision];

  return (
    <div className="ac-modal-ov" onClick={onCancel}>
      <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="ac-modal-title">{title}</h3>
        <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.8, color: 'var(--ac-muted)' }}>{blurb}</p>

        {supplier.factory?.id && (
          <p className="acv-modal-note">
            {decision === 'approve'
              ? (isAr ? 'شارة «موثّق» على صفحة المصنع أمام التجار ستُفعَّل.' : 'The Verified badge on the public factory page will be switched on.')
              : (isAr ? 'شارة «موثّق» على صفحة المصنع أمام التجار ستُطفأ.' : 'The Verified badge on the public factory page will be switched off.')}
          </p>
        )}

        {decision === 'approve' && missingFields.length > 0 && (
          <p className="acv-modal-warn">
            {isAr
              ? `تنبيه: ما زالت هذه الحقول فارغة — ${missingFields.map((f) => f.ar).join('، ')}.`
              : `Heads up: these fields are still empty — ${missingFields.map((f) => f.en).join(', ')}.`}
          </p>
        )}

        <div className="ac-field wide" style={{ marginTop: 4 }}>
          <label className="ac-flabel" htmlFor="acv-note">
            {spec.needsNote
              ? (isAr ? 'السبب — يصل المورّد *' : 'Reason — the supplier sees this *')
              : (isAr ? 'ملاحظة داخلية (اختياري)' : 'Internal note (optional)')}
          </label>
          <textarea id="acv-note" className="ac-textarea" value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder={spec.needsNote
                      ? (isAr ? 'مثال: صورة الرخصة غير واضحة، أعد رفعها بدقة أعلى.' : 'e.g. The licence photo is unreadable — please re-upload it at a higher resolution.')
                      : (isAr ? 'يُحفظ في سجل المراجعة' : 'Kept in the review log')} />
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" className="ac-btn" onClick={onCancel} disabled={busy}>{isAr ? 'إلغاء' : 'Cancel'}</button>
          <button type="button" className="ac-btn ac-btn-primary" disabled={busy || (spec.needsNote && !nf(note))}
                  onClick={() => onConfirm(note)}>
            {busy ? (isAr ? 'جارٍ...' : 'Working…') : title}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── The verification file ───────────────────────────────────────────────────
function VerificationFile({ row, user, isAr, onDecided, flash }) {
  const docs = useMemo(() => buildDocList(row), [row]);
  const { urls, failed, onLoadError, ensureFresh, retry } = useSignedDocs(docs);
  const [checked, setChecked] = useState(() => new Set());
  const [lbIndex, setLbIndex] = useState(null);
  const [decision, setDecision] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [products, setProducts] = useState(null);
  const journey = useMemo(() => buildSupplierJourney(row, history), [row, history]);

  useEffect(() => { setChecked(new Set()); setLbIndex(null); }, [row.id]);
  useEffect(() => { let alive = true; fetchReviewHistory(row.id).then((h) => { if (alive) setHistory(h); }); return () => { alive = false; }; }, [row.id]);
  useEffect(() => { let alive = true; setProducts(null); fetchSupplierProducts(row.id).then((p) => { if (alive) setProducts(p); }); return () => { alive = false; }; }, [row.id]);

  const toggle = useCallback((docId) => {
    setChecked((prev) => { const next = new Set(prev); if (next.has(docId)) next.delete(docId); else next.add(docId); return next; });
  }, []);

  const openDoc = useCallback((doc) => {
    const i = docs.findIndex((d) => d.id === doc.id);
    if (i < 0) return;
    ensureFresh(doc);
    setLbIndex(i);
  }, [docs, ensureFresh]);

  const navLightbox = useCallback((i) => {
    if (i < 0 || i >= docs.length) return;
    ensureFresh(docs[i]);
    setLbIndex(i);
  }, [docs, ensureFresh]);

  const requiredDocs = docs.filter((d) => d.required);
  const requiredReviewed = requiredDocs.every((d) => checked.has(d.id));
  const missingFields = row.completeness.items.filter((i) => !i.ok);
  const name = nf(row.company_name) || nf(row.full_name) || (isAr ? 'بلا اسم' : 'Unnamed');
  const latin = nf(row.company_name_latin) && row.company_name_latin !== row.company_name ? row.company_name_latin : '';
  const isDecided = row.bucket === 'verified' || row.bucket === 'rejected';

  const confirm = async (note) => {
    setBusy(true);
    try {
      const { warnings } = await applyVerificationDecision({ supplier: row, decision, note, actorId: user.id });
      const msgs = [];
      if (warnings.includes('note_column_missing')) msgs.push(isAr ? 'حُفظ السبب في السجل فقط (العمود غير مُطبَّق بعد)' : 'Reason kept in the log only (column not applied yet)');
      if (warnings.includes('email_failed')) msgs.push(isAr ? 'لم تُرسل الرسالة للمورّد' : 'The supplier email did not send');
      if (warnings.includes('no_email')) msgs.push(isAr ? 'لا يوجد بريد للمورّد' : 'No email on file');
      if (warnings.includes('factory_badge_failed')) msgs.push(isAr ? 'تعذّر تحديث شارة المصنع' : 'Factory badge not updated');
      flash(msgs.length ? `${isAr ? 'تم — لكن: ' : 'Done — but: '}${msgs.join(' · ')}` : (isAr ? 'تم تسجيل القرار' : 'Decision recorded'));
      setDecision(null);
      await onDecided();
    } catch (e) {
      flash(`${isAr ? 'فشل: ' : 'Failed: '}${e?.message || e}`);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="acv-file-head">
        {row.avatar_url ? <img className="ac-dlogo" src={row.avatar_url} alt="" /> : <div className="ac-dlogo-fb">{name.charAt(0).toUpperCase()}</div>}
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 className="ac-dname">{name}</h2>
          {latin && <p className="ac-dlatin" dir="ltr" style={{ textAlign: isAr ? 'right' : 'left' }}>{latin}</p>}
          <div className="ac-quick" style={{ marginTop: 10 }}>
            <StatusBadge status={row.status} isAr={isAr} />
            {row.origin === 'factory_claim' && <span className="ac-badge info"><span className="dot" />{isAr ? 'مطالبة مصنع' : 'Factory claim'}</span>}
            {row.factory?.is_verified && <span className="ac-badge ok"><span className="dot" />{isAr ? 'شارة موثّق ظاهرة' : 'Badge live'}</span>}
            {row.duplicateName && <span className="ac-badge warn"><span className="dot" />{isAr ? 'اسم مكرر' : 'Duplicate name'}</span>}
          </div>
          <p className="acv-file-sub">
            {nf(row.maabar_supplier_id) && <span dir="ltr">{row.maabar_supplier_id}</span>}
            {nf(row.maabar_supplier_id) && <span className="acv-dot-sep">·</span>}
            {isAr ? 'سجَّل في ' : 'Registered '}{fmtDate(row.created_at, isAr)}
          </p>
        </div>
        <button
          type="button"
          className="ac-btn ac-btn-sm"
          disabled={printing}
          onClick={async () => {
            setPrinting(true);
            const ok = await printVerificationFile(row, { isAr });
            setPrinting(false);
            if (!ok) flash(isAr ? 'المتصفح منع النافذة — اسمح بالنوافذ المنبثقة' : 'The browser blocked the window — allow pop-ups');
          }}
        >
          {printing ? '…' : (isAr ? 'تنزيل PDF' : 'Download PDF')}
        </button>
      </div>

      <JourneyCard journey={journey} isAr={isAr} />

      {/* Documents */}
      <div className="ac-card">
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <p className="ac-card-title" style={{ marginBottom: 10 }}>{isAr ? 'الوثائق' : 'Documents'}</p>
          {requiredDocs.length > 0 && (
            <p className={`acv-reviewed${requiredReviewed ? ' done' : ''}`}>
              {isAr
                ? `${requiredDocs.filter((d) => checked.has(d.id)).length}/${requiredDocs.length} وثيقة مطلوبة روجعت`
                : `${requiredDocs.filter((d) => checked.has(d.id)).length}/${requiredDocs.length} required documents reviewed`}
            </p>
          )}
        </div>
        {docs.length === 0 ? (
          <p className="ac-placeholder">{isAr ? 'لم يرفع هذا المورّد أي وثيقة بعد.' : 'This supplier has not uploaded any documents yet.'}</p>
        ) : (
          <div className="acv-docgrid">
            {docs.map((doc) => (
              <DocCard key={doc.id} doc={doc} url={urls[doc.id]} failed={failed[doc.id]} checked={checked.has(doc.id)}
                       onToggle={toggle} onOpen={openDoc} onLoadError={onLoadError} onRetry={retry} isAr={isAr} />
            ))}
          </div>
        )}
      </div>

      {/* Completeness */}
      <div className="ac-card">
        <p className="ac-card-title">{isAr ? 'اكتمال الملف' : 'File completeness'}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div className="ac-meter" style={{ flex: 1 }}><span style={{ width: `${row.completeness.percent}%` }} /></div>
          <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{row.completeness.done}/{row.completeness.total}</span>
        </div>
        <div className="acv-fieldgrid">
          {row.completeness.items.map((f) => (
            <span key={f.key} className={`acv-fieldchip${f.ok ? ' ok' : ''}`}>
              <span aria-hidden="true">{f.ok ? '✓' : '○'}</span>{isAr ? f.ar : f.en}
            </span>
          ))}
        </div>
      </div>

      <SupplierOutreach row={row} journey={journey} history={history} user={user} isAr={isAr} flash={flash}
                        onSent={() => fetchReviewHistory(row.id).then(setHistory)} />

      {/* Company details */}
      <div className="ac-card">
        <p className="ac-card-title">{isAr ? 'بيانات الشركة' : 'Company details'}</p>
        <KV k={isAr ? 'رقم السجل' : 'Registration no.'} v={nf(row.reg_number) || nf(row.company_reg_number)} mono />
        <KV k={isAr ? 'سنوات الخبرة' : 'Years of experience'} v={row.years_experience} mono />
        <KV k={isAr ? 'عدد الموظفين' : 'Employees'} v={row.num_employees} mono />
        <KV k={isAr ? 'نوع النشاط' : 'Business type'} v={row.business_type} />
        <KV k={isAr ? 'التخصص' : 'Speciality'} v={row.speciality} />
        <KV k={isAr ? 'المدينة / الدولة' : 'City / Country'} v={[nf(row.city), nf(row.country)].filter(Boolean).join('، ')} />
        <KV k={isAr ? 'الرابط التجاري' : 'Trade link'} v={row.trade_link} link />
        <KV k={isAr ? 'الموقع' : 'Website'} v={row.company_website} link />
        <KV k={isAr ? 'أسواق التصدير' : 'Export markets'} v={Array.isArray(row.export_markets) ? row.export_markets.join('، ') : row.export_markets} />
        <KV k={isAr ? 'نبذة' : 'Description'} v={row.company_description} />
      </div>

      {/* Contact + cross-checks */}
      <div className="ac-card">
        <p className="ac-card-title">{isAr ? 'التواصل والفحوصات' : 'Contact & cross-checks'}</p>
        <KV k={isAr ? 'البريد' : 'Email'} v={row.email} mono />
        <KV k="WhatsApp" v={row.whatsapp} mono />
        <KV k="WeChat" v={row.wechat} mono />
        <KV k={isAr ? 'الهاتف' : 'Phone'} v={row.phone} mono />
        <KV k={isAr ? 'المنتجات' : 'Products'} v={String(row.productCount)} mono />
        {row.factory && (
          <KV k={isAr ? 'المصنع المرتبط' : 'Linked factory'}
              v={`${nf(row.factory.company_name) || row.factory.id}${row.factory.claimed_at ? ` — ${fmtDate(row.factory.claimed_at, isAr)}` : ''}`} />
        )}
        {row.duplicateName && (
          <p className="acv-modal-warn" style={{ marginTop: 12 }}>
            {isAr ? 'يوجد حساب آخر بنفس اسم الشركة — تحقّق قبل التوثيق.' : 'Another account carries the same company name — check before verifying.'}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
          {row.factory && (
            <a className="ac-btn ac-btn-sm" href={`/factory/${row.factory.id}`} target="_blank" rel="noreferrer noopener">
              {isAr ? 'صفحة المصنع العامة ↗' : 'Public factory page ↗'}
            </a>
          )}

        </div>
      </div>

      {/* Products — the cross-check, not a management screen */}
      <div className="ac-card">
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <p className="ac-card-title">{isAr ? 'منتجاته' : 'Products'}</p>
          <p className="acv-lastreach">{isAr ? `${row.productCount} إجمالاً` : `${row.productCount} total`}</p>
        </div>
        {products === null ? (
          <div className="ac-skel" style={{ height: 60 }} />
        ) : products.failed ? (
          <p className="acv-modal-warn">{isAr ? 'تعذّر تحميل المنتجات — راجع الكونسول.' : 'Could not load products — see the console.'}</p>
        ) : products.rows.length === 0 ? (
          <p className="ac-placeholder">{isAr ? 'لا منتجات على هذا الحساب.' : 'No products on this account.'}</p>
        ) : (
          <ul className="acv-prodlist">
            {products.rows.map((p) => (
              <li key={p.id} className="acv-prod">
                <span className="acv-prod-name">{p.name_en || p.name_zh || p.name_ar || p.id}</span>
                <span className="acv-prod-meta">
                  {p.is_active ? (isAr ? 'نشط' : 'active') : (isAr ? 'غير نشط' : 'inactive')}
                  {p.moq ? <><span className="acv-dot-sep">·</span>{isAr ? `أقل كمية ${p.moq}` : `MOQ ${p.moq}`}</> : null}
                  {p.has_variants ? <><span className="acv-dot-sep">·</span>{isAr ? 'متغيّرات' : 'variants'}</> : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* What the supplier has done: offers and conversations (owner, 2026-10-11) */}
      <SupplierActivity supplierId={row.id} factoryId={row.factory?.id || null} isAr={isAr} />

      {/* Internal notes — the same thread the legacy file showed, same table */}
      <div className="ac-card">
        <p className="ac-card-title">{isAr ? 'ملاحظات داخلية' : 'Internal notes'}</p>
        <AdminNoteThread entityType="supplier" entityId={row.id} user={user} lang={isAr ? 'ar' : 'en'} />
      </div>

      {/* Review history */}
      {history.length > 0 && (
        <div className="ac-card">
          <p className="ac-card-title">{isAr ? 'سجل المراجعة' : 'Review log'}</p>
          {history.map((h) => (
            <div key={h.id} className="acv-hist">
              <span className="acv-hist-when">{fmtDate(h.created_at, isAr)}</span>
              <span className="acv-hist-what">
                <b>{h.action}</b>
                {h.actorName && <span className="acv-hist-who"> — {h.actorName}</span>}
                {nf(h.notes) && <span className="acv-hist-note">{h.notes}</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Decision */}
      <div className="acv-decide">
        {isDecided ? (
          <p className="acv-decide-hint">
            {row.bucket === 'verified'
              ? (isAr ? 'هذا الحساب موثّق. يمكنك سحب التوثيق بالرفض.' : 'This account is verified. Rejecting withdraws it.')
              : (isAr ? 'هذا الحساب مرفوض. يمكنك توثيقه إن استجدّ شيء.' : 'This account is rejected. You can still verify it if something changed.')}
          </p>
        ) : !requiredReviewed ? (
          <p className="acv-decide-hint">
            {docs.length === 0
              ? (isAr ? 'لا توجد وثائق للمراجعة — لا يمكن التوثيق.' : 'No documents to review — verification is not possible.')
              : (isAr ? 'علّم كل وثيقة مطلوبة بعد مراجعتها ليُفتح زر التوثيق.' : 'Tick every required document once reviewed to unlock verification.')}
          </p>
        ) : (
          <p className="acv-decide-hint done">{isAr ? 'روجعت كل الوثائق المطلوبة.' : 'All required documents reviewed.'}</p>
        )}
        <div className="acv-decide-btns">
          <button type="button" className="ac-btn acv-btn-danger" onClick={() => setDecision('reject')}>{isAr ? 'رفض' : 'Reject'}</button>
          <button type="button" className="ac-btn" onClick={() => setDecision('more_info')}>{isAr ? 'طلب استكمال' : 'Request info'}</button>
          <button type="button" className="ac-btn ac-btn-primary" disabled={!requiredReviewed || row.bucket === 'verified'}
                  onClick={() => setDecision('approve')}>{isAr ? 'توثيق ✓' : 'Verify ✓'}</button>
        </div>
      </div>

      {lbIndex !== null && (
        <DocLightbox docs={docs} urls={urls} failed={failed} index={lbIndex} onIndex={navLightbox}
                     onClose={() => setLbIndex(null)} onLoadError={onLoadError} onRetry={retry}
                     checked={checked} onToggle={toggle} isAr={isAr} />
      )}
      {decision && (
        <DecisionModal decision={decision} supplier={row} missingFields={missingFields} busy={busy}
                       onCancel={() => setDecision(null)} onConfirm={confirm} isAr={isAr} />
      )}
    </>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function ConsoleVerification({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('ready');
  const [q, setQ] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try { setData(await fetchVerificationQueue()); }
    catch (e) { setErr(e?.message || 'load failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 4200); };

  const selected = useMemo(() => (data && id ? data.rows.find((r) => r.id === id) || null : null), [data, id]);

  // Follow the open file into its new bucket after a decision, so the tab you
  // are looking at is never one the selected supplier just left.
  useEffect(() => { if (selected && selected.bucket !== tab) setTab(selected.bucket); }, [selected?.id, selected?.bucket]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = useMemo(() => {
    if (!data) return [];
    const needle = nf(q).toLowerCase();
    return data.buckets[tab].filter((r) => !needle
      || nf(r.company_name).toLowerCase().includes(needle)
      || nf(r.company_name_latin).toLowerCase().includes(needle)
      || nf(r.full_name).toLowerCase().includes(needle)
      || nf(r.email).toLowerCase().includes(needle)
      || nf(r.maabar_supplier_id).toLowerCase().includes(needle));
  }, [data, tab, q]);

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="suppliers">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            {/* It was called "Factory Verification" and it has never listed a
                factory: every row is a supplier account. That inversion is the
                one the console reorg exists to undo, so the title goes with it. */}
            <h1 className="ac-h1">{isAr ? 'مراجعة المورّدين' : 'Supplier review'}</h1>
            <p className="ac-sub">
              {isAr
                ? 'راجع الوثائق واتّخذ القرار من هنا — بدون فتح أي ملف خارج الصفحة.'
                : 'Review the documents and decide here — without opening a single file outside this page.'}
            </p>
          </div>
        </div>

        {err && <p style={{ color: 'var(--ac-danger)' }}>{err}</p>}

        <div className="ac-tabs">
          {QUEUE_TABS.map((t) => (
            <button key={t.key} type="button" className={`ac-tab${tab === t.key ? ' on' : ''}`} onClick={() => setTab(t.key)}>
              {isAr ? t.ar : t.en}
              <span className="ac-tab-badge">{data ? data.buckets[t.key].length : '—'}</span>
            </button>
          ))}
        </div>

        <div className="acv-split">
          <aside className="acv-queue">
            <input className="ac-input" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder={isAr ? 'ابحث بالاسم أو البريد أو رقم المورّد' : 'Search name, email or supplier ID'}
                   style={{ marginBottom: 10 }} />
            {!data ? (
              <div className="ac-skel" style={{ height: 220 }} />
            ) : list.length === 0 ? (
              <p className="ac-placeholder">{isAr ? 'لا شيء هنا.' : 'Nothing here.'}</p>
            ) : (
              <div className="acv-rows">
                {list.map((r) => <QueueRow key={r.id} row={r} active={r.id === id} isAr={isAr} onOpen={(rid) => nav(`/admin2/verification/${rid}`)} />)}
              </div>
            )}
          </aside>

          <section className="acv-file">
            {!data ? <div className="ac-skel" style={{ height: 320 }} />
              : !selected ? (
                <div className="ac-card">
                  <p className="ac-placeholder">
                    {isAr ? 'اختر مصنعاً من القائمة لبدء المراجعة.' : 'Pick a factory from the list to start reviewing.'}
                  </p>
                </div>
              ) : (
                <VerificationFile row={selected} user={user} isAr={isAr} flash={flash} onDecided={load} />
              )}
          </section>
        </div>
      </div>
      {toast && <div className="ac-toast">{toast}</div>}
    </ConsoleShell>
  );
}
